import { useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useState } from 'react';
import { t } from '../i18n';
import { api, downloadFile } from '../lib/api';
import { ATTACHMENT_ACCEPT, type Attachment, attachmentsKey, formatSize, uploadAttachment, useAttachments } from '../lib/attachments';
import { useMe } from '../lib/auth';
import { errorMessage } from '../lib/errors';
import { Alert, Button, Card, Spinner } from './ui';

const formatDateTime = (iso: string) => new Date(iso).toLocaleString('es-PA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

interface Props {
  /** Tipo de entidad registrado en la API (ticket, project_request, project…). */
  entity: string;
  entityId: string;
  /** Puede subir archivos (y borrar los suyos). */
  canUpload: boolean;
  /** Puede borrar los de otros. */
  canManage: boolean;
  /** Ayuda bajo el botón de subir (ej. quién ve los archivos). */
  help?: string;
}

/** Adjuntos de una entidad: capturas, documentos, registros. Quién los ve y los sube lo decide la API. */
export function AttachmentsCard({ entity, entityId, canUpload, canManage, help }: Props) {
  const me = useMe();
  const qc = useQueryClient();
  const attachments = useAttachments(entity, entityId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Attachment | null>(null);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: attachmentsKey(entity, entityId) });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const upload = (e: ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = ''; // permite elegir el mismo archivo otra vez
    if (files.length === 0) return;
    void act(async () => {
      for (const file of files) await uploadAttachment(entity, entityId, file);
    });
  };

  // Lo propio se borra mientras se pueda subir; lo de otro, solo quien gestiona.
  const canDelete = (a: Attachment) => canManage || (canUpload && a.uploadedBy === me.id);

  // Quien solo mira no necesita una tarjeta vacía (el lector de un artículo, un ticket cancelado).
  if (!canUpload && attachments.data?.length === 0) return null;

  return (
    <Card variant="aside" title={t('adj.titulo')}>
      <div className="space-y-3">
        {error && <Alert>{error}</Alert>}
        {!attachments.data ? (
          attachments.error ? <Alert>{errorMessage(attachments.error)}</Alert> : <Spinner label={t('comun.cargando')} />
        ) : attachments.data.length === 0 ? (
          <p className="text-sm text-slate-500">{t('adj.vacio')}</p>
        ) : (
          <ul className="divide-y divide-slate-100" aria-label={t('adj.titulo')}>
            {attachments.data.map((a) => (
              <li key={a.id} className="flex items-start justify-between gap-2 py-2">
                <span className="min-w-0 text-sm">
                  <Button variant="link" className="break-all text-left" onClick={() => void act(() => downloadFile(`/api/adjuntos/${a.id}/descarga`, a.fileName))}>
                    {a.fileName}
                  </Button>
                  <span className="block text-xs text-slate-500">
                    {formatSize(a.sizeBytes)} · {a.uploadedByName ?? '—'} · {formatDateTime(a.createdAt)}
                  </span>
                </span>
                {canDelete(a) && (
                  <button
                    type="button"
                    className="px-1 text-slate-400 hover:text-red-700"
                    aria-label={`${t('plataforma.borrar')} ${a.fileName}`}
                    onClick={() => setConfirming(a)}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {confirming && (
          <Alert tone="warning">
            <p>{t('adj.borrar_confirmar', { nombre: confirming.fileName })}</p>
            <div className="mt-2 flex gap-2">
              <Button
                variant="danger"
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    await api(`/api/adjuntos/${confirming.id}`, { method: 'DELETE' });
                    setConfirming(null);
                  })
                }
              >
                {t('acceso.si_borrar')}
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(null)}>
                {t('comun.cancelar')}
              </Button>
            </div>
          </Alert>
        )}
        {canUpload && (
          <div className="space-y-1">
            <label className="inline-block">
              <span className="sr-only">{t('adj.adjuntar')}</span>
              <input type="file" multiple accept={ATTACHMENT_ACCEPT} disabled={busy} onChange={upload} aria-label={t('adj.adjuntar')} className="text-sm" />
            </label>
            <p className="text-xs text-slate-500">{busy ? t('adj.subiendo') : (help ?? t('adj.ayuda'))}</p>
          </div>
        )}
      </div>
    </Card>
  );
}
